import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGOS, BRANCH_LIST } from "@/lib/brand";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/a-propos", label: "À propos" },
  { to: "/studio", label: "Studio" },
  { to: "/securite", label: "Sécurité" },
  { to: "/proprete", label: "Propreté" },
  { to: "/contact", label: "Contact" },
];

function getActiveAccent(pathname) {
  if (pathname.startsWith("/studio")) return "#8b5cf6";
  if (pathname.startsWith("/securite")) return "#ea580c";
  if (pathname.startsWith("/proprete")) return "#0ea5e9";
  return "#ffffff";
}

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const accent = getActiveAccent(location.pathname);

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-logo">
          <img src={LOGOS.groupe} alt="Equinoxe Groupe" className="h-12 w-12 object-contain" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-base font-semibold tracking-tight">EQUINOXE</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Groupe</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span
                      className="absolute -bottom-[26px] left-1/2 -translate-x-1/2 h-[2px] w-8"
                      style={{ backgroundColor: accent }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/espace-client/login"
            data-testid="nav-cta-espace-client"
            className="px-5 py-2.5 rounded-md text-sm font-semibold transition-all border border-white/20 text-white hover:bg-white/5"
          >
            Espace client
          </Link>
          <Link
            to="/contact"
            data-testid="nav-cta-devis"
            className="px-5 py-2.5 rounded-md text-sm font-semibold transition-all border"
            style={{
              backgroundColor: accent === "#ffffff" ? "#ffffff" : accent,
              color: accent === "#ffffff" ? "#0a0a0a" : "#ffffff",
              borderColor: accent === "#ffffff" ? "#ffffff" : accent,
            }}
          >
            Demander un devis
          </Link>
        </div>

        <button
          className="lg:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl" data-testid="mobile-nav">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-sm ${isActive ? "bg-white/10 text-white" : "text-zinc-400"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/espace-client/login"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-espace-client"
              className="mt-2 px-3 py-3 rounded-md border border-white/20 text-white text-sm font-semibold text-center"
            >
              Espace client
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-cta"
              className="mt-2 px-3 py-3 rounded-md bg-white text-black text-sm font-semibold text-center"
            >
              Demander un devis
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
